import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Language_List } from '../helper/Language_Utils';
import YouTubeVideoPopup from './YouTubeVideoPopup';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagic,
  faLightbulb,
  faCheckCircle,
  faScroll,
  faImage,
  faRoute,
  faPlay,
  faChalkboard,
  faPlus,
  faRobot,
  faPenToSquare,
  faMicroscope,
  faXRay,
  faClipboardList,
  faBone,
  faExclamationTriangle,
  faGraduationCap,
  faFlask,
  faHospital,
  faDna,
  faCode,
  faAtom,
  faGlobe,
  faCubes,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { UserInfo, VisualCache } from '../helper/Context';
import { FileUploadForm, WebUploadForm } from './CreateVisForms';
import SearchComponent from './SearchComponent';
import CarouselVisCard from './CarouselVisCard';
import CarouselPresentationCard from './CarouselPresentationCard';
import CarouselExplainerCard from './CarouselExplainerCard';
import VisualPickerModal from './VisualPickerModal';
import PremiumFeatureModal from './PremiumFeatureModal';
import AccessCodeModal from './AccessCodeModal';
import { shallowCopy } from '../helper/Utils';
import { APIAddEditPresentation, APIWebVisualization, APISearchVisuals } from '../helper/APIFunctions';
import { Typeahead } from 'react-bootstrap-typeahead';
import './ContentCreationWizard.css';

let anatomySuggestions = [];
const keepLowerCase = ["And", "Or", "The", "Of", "In", "On", "At", "To"];
const titleCase = (str) => {
  // Convert the whole string to lowercase first for consistency
  str = str.toLowerCase();

  // Use replace() with a regex to find the first letter of each word (/\b\w/g)
  // and replace it with its uppercase equivalent.

  str = str.replace(/\b\w/g, (char) => char.toUpperCase());
  for (const word of keepLowerCase) {
    str = str.replace(new RegExp(`\\b${word}\\b`, 'g'), word.toLowerCase());
  }
  return str;
};
const excludeNames = ["genital", "reproductive", "penis", "scrotum", "female", "male", "urethra"]; // filter out these names from the suggestions for the time being.
fetch('https://vis.snorkle.io/resources/anatomy_directories/20260612/names_codes_20260612.json')
  .then(response => response.json())
  .then(data => {
    for (const [name, code] of Object.entries(data.names2codes)) {
      const lowerName = name.toLowerCase();
      if (excludeNames.some(exclude => lowerName.includes(exclude))) {
        continue;
      }
      anatomySuggestions.push({
        id: code,
        entry_key: name,
        name: titleCase(name),
      });
    }
    const neuronNames = ["Neuron", "Neural Cell", "Nerve Cell"];
    const parts = {"Axon": "AXON", "Dendrite": "DENDRITE", "Soma": "SOMA", "Cell Body": "SOMA", "Axon Hillock": "AXON_HILLOCK"};
    for (const name of neuronNames) {
      for (const [part, code] of Object.entries(parts)) {
        anatomySuggestions.push({
          id: `NEURON:${code}`,
          entry_key: `${part} (${name})`,
          name: titleCase(`${part} (${name})`),
        });
      }
      anatomySuggestions.push({
        id: `NEURON:ALL`,
        entry_key: name,
        name: titleCase(name),
      });
    }
    anatomySuggestions.sort((a, b) => (a.name.length - b.name.length) || a.name.localeCompare(b.name));
  })
  .catch(error => {
    console.warn('Error loading anatomy suggestions:', error);
  });

const GENERATION_STEPS = [
  { label: 'Researching relevant articles…',   duration: 5000 },
  { label: 'Parsing references and citations…', duration: 5000 },
  { label: 'Double-checking sources…',          duration: 5000 },
  { label: 'Composing content…',                duration: 6000 },
  { label: 'Finalizing suggestions…',           duration: 0    },
];

const WizardGeneratingOverlay = () => {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const step = GENERATION_STEPS[stepIndex];
    if (!step || step.duration === 0) return;
    const timer = setTimeout(() => {
      setStepIndex(i => Math.min(i + 1, GENERATION_STEPS.length - 1));
    }, step.duration);
    return () => clearTimeout(timer);
  }, [stepIndex]);

  return (
    <div className="wizard-generating-overlay">
      <div className="ai-draft-loader wizard-gen-loader" />
      <p className="wizard-gen-status-label">{GENERATION_STEPS[stepIndex].label}</p>
      <div className="wizard-gen-steps">
        {GENERATION_STEPS.map((s, i) => (
          <div
            key={i}
            className={`wizard-gen-step ${i < stepIndex ? 'done' : i === stepIndex ? 'active' : ''}`}
          >
            <span className="wizard-gen-step-dot" />
            <span className="wizard-gen-step-text">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ContentCreationWizard = ({
  show,
  onHide,
  userSettings,
  onUpdateUserSettings,
  onGenerate,
  isGenerating,
  generationProgress,
  generatedContent,
  generationError,
  onReset,
  onOpenManualPicker,
  visuals,
  presentations,
  explainers,
  videos,
  fromChannel = false // Flag to indicate if wizard was opened from channel dashboard
}) => {
  const navigate = useNavigate();
  const { userInfo } = useContext(UserInfo);
  const { visCache, setVisCache } = useContext(VisualCache);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedHub, setSelectedHub] = useState(null); // Top-level hub category
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [creationMethod, setCreationMethod] = useState(null); // 'ai' or 'manual'

  // State for manual standalone visual flow
  const [hasOwnResources, setHasOwnResources] = useState(null);
  const [manualSourceType, setManualSourceType] = useState(null);
  const [requestDOI, setRequestDOI] = useState(false);
  const [visualType, setVisualType] = useState(null); // New state for visual type selection

  // State for AI standalone visual search
  const [aiSearchTerm, setAISearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // State for successful visual creation
  const [createdVisual, setCreatedVisual] = useState(null);

  // State for viewing examples
  const [viewingExamples, setViewingExamples] = useState(false);
  const [viewingVisualTypeExamples, setViewingVisualTypeExamples] = useState(false);

  // State for quiz builder
  const [quizTitle, setQuizTitle] = useState('');
  const [quizQuestion, setQuizQuestion] = useState('');
  const [quizAnswers, setQuizAnswers] = useState([
    { id: `ans-${Date.now()}-0`, text: '' },
    { id: `ans-${Date.now()}-1`, text: '' },
  ]);
  const [quizCorrectIndex, setQuizCorrectIndex] = useState(0);
  const [quizVisual, setQuizVisual] = useState(null); // vis object from library
  const [showQuizVisualPicker, setShowQuizVisualPicker] = useState(false);
  const [isSavingQuiz, setIsSavingQuiz] = useState(false);
  const [quizSaveError, setQuizSaveError] = useState(null);
  const [selectedAnatomy, setSelectedAnatomy] = useState(null);
  const [isCreatingAnatomy, setIsCreatingAnatomy] = useState(false);
  const [anatomyCreationError, setAnatomyCreationError] = useState(null);

  // Premium gating
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeatureName, setPremiumFeatureName] = useState('');
  const isFreeUser = !userInfo?.tier || userInfo?.tier === 'free';

  // Access code gating (experimental features)
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [accessCodeFeatureName, setAccessCodeFeatureName] = useState('');
  const [accessCodePermission, setAccessCodePermission] = useState('');
  const [accessCodeAction, setAccessCodeAction] = useState(null); // fn to call on granted

  // Hub card GIF preview
  const [hoveredHubPreview, setHoveredHubPreview] = useState(null); // { gif, rect }
  const gifPreviewTimerRef = useRef(null);

  // Clear the GIF preview whenever the step changes or the wizard closes —
  // cards that trigger a step change unmount before onMouseLeave can fire.
  useEffect(() => {
    clearTimeout(gifPreviewTimerRef.current);
    setHoveredHubPreview(null);
  }, [step, show]);

  // Visual picking step state (after approving AI scrollytelling script)
  const [visualPickingMode, setVisualPickingMode] = useState(false);
  const [visualPickingIdx, setVisualPickingIdx] = useState(0);
  const [slideSearchResults, setSlideSearchResults] = useState([]); // [{status, results}] per slide
  const [approvedVisuals, setApprovedVisuals] = useState([]); // selected item (or null) per slide
  const [pendingVariation, setPendingVariation] = useState(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [showAllPickerResults, setShowAllPickerResults] = useState(false);
  const [showSlideVisualPicker, setShowSlideVisualPicker] = useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log('🔧 Wizard State Changed:', {
      step,
      selectedContentType,
      creationMethod,
      hasOwnResources,
      manualSourceType,
      requestDOI,
      show
    });
  }, [step, selectedContentType, creationMethod, hasOwnResources, manualSourceType, requestDOI, show]);

  // Advance to results when content is generated
  React.useEffect(() => {
    if (generatedContent && creationMethod === 'ai' && !isGenerating) {
      setStep(5);
    }
  }, [generatedContent, creationMethod, isGenerating]);

  // Helper: get thumbnail URL for a search result item
  const getResultThumbnail = (item) => {
    if (!item) return null;
    if (item.thumbnail) return item.thumbnail;
    if (item.type === 'images') return item.url;
    if (item.type === 'pdb') return `https://cdn.rcsb.org/images/structures/${item.id.toLowerCase()}_assembly-1.jpeg`;
    if (item.type === 'pubchem') return `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/cid/${item.id}/PNG?record_type=3d`;
    if (item.type === 'anatomy-set') return '/snorkle_vis_thumbnails/anatomy_set_thumb.png';
    return null;
  };

  // Helper: readable type label for a search result item
  const getResultTypeLabel = (item) => {
    const labels = {
      pdb: 'Molecule / PDB',
      pubchem: 'Small Molecule',
      models: '3D Model',
      'anatomy-set': 'Anatomy Viewer',
      snorkle: 'Snorkle Visual',
      images: 'Image',
    };
    return labels[item?.type] || item?.type || 'Visual';
  };

  // Helper: create a Snorkle library visual from a search result item
  const createVisualFromItem = (item) => {
    return new Promise((resolve) => {
      const typeMap = {
        pdb: 'pdb',
        pubchem: 'pubchem',
        models: 'sketchfab',
        'anatomy-set': 'anatomy_set',
        snorkle: 'snorkle',
      };
      const resourceType = typeMap[item.type];
      if (!resourceType) { resolve(null); return; }
      APIWebVisualization(
        item.id,
        resourceType,
        item.title,
        item.description || '',
        (res) => resolve(res),
        () => resolve(null),
        false
      );
    });
  };

  // Enter the visual picking step (called when user approves the AI script)
  const handleEnterVisualPicker = (variation) => {
    const slidesArray = Array.isArray(variation.slides) ? variation.slides : [];
    if (slidesArray.length === 0) {
      console.error('Invalid slides data:', variation);
      alert('Error: Unable to process slides data. Please try generating again.');
      return;
    }
    setPendingVariation(variation);
    setApprovedVisuals(new Array(slidesArray.length).fill(null));
    setSlideSearchResults(new Array(slidesArray.length).fill({ status: 'idle', results: [] }));
    setVisualPickingIdx(0);
    setVisualPickingMode(true);
    // Kick off search for the first slide immediately
    fetchSlideSearchResults(variation, 0, slidesArray);
  };

  // Fetch search results for slide at index i
  const fetchSlideSearchResults = (variation, idx, slidesArray) => {
    const slides = slidesArray || (Array.isArray(variation?.slides) ? variation.slides : []);
    const slide = slides[idx];
    if (!slide) return;
    const visualRaw = slide.visual || '';
    const match = visualRaw.match(/^(?:molecule|anatomy|sketchfab|imaging):\s*(.+)$/i);
    const searchTerm = match ? match[1].trim() : visualRaw.trim();
    if (!searchTerm) return;

    setSlideSearchResults(prev => {
      const next = [...prev];
      next[idx] = { status: 'loading', results: [] };
      return next;
    });
    APISearchVisuals(
      searchTerm,
      (res) => {
        const results = res?.sources || [];
        setSlideSearchResults(prev => {
          const next = [...prev];
          next[idx] = { status: 'done', results };
          return next;
        });
      },
      () => {
        setSlideSearchResults(prev => {
          const next = [...prev];
          next[idx] = { status: 'error', results: [] };
          return next;
        });
      }
    );
  };

  // Advance visual picker to next slide (or finish)
  const handleVisualPickerAdvance = (selectedItem) => {
    const slides = Array.isArray(pendingVariation?.slides) ? pendingVariation.slides : [];
    const newApproved = [...approvedVisuals];
    newApproved[visualPickingIdx] = selectedItem || null;
    setApprovedVisuals(newApproved);
    setShowAllPickerResults(false);
    setShowSlideVisualPicker(false);

    const nextIdx = visualPickingIdx + 1;
    if (nextIdx >= slides.length) {
      handleFinalizeWithVisuals(newApproved);
    } else {
      setVisualPickingIdx(nextIdx);
      if (slideSearchResults[nextIdx]?.status === 'idle') {
        fetchSlideSearchResults(pendingVariation, nextIdx, slides);
      }
    }
  };

  // Go back one slide in the visual picker
  const handleVisualPickerBack = () => {
    setShowAllPickerResults(false);
    setShowSlideVisualPicker(false);
    if (visualPickingIdx === 0) {
      setVisualPickingMode(false);
      setPendingVariation(null);
    } else {
      setVisualPickingIdx(idx => idx - 1);
    }
  };

  // Finalize: create visuals for selected items then navigate to builder
  const handleFinalizeWithVisuals = async (finalApproved) => {
    const variation = pendingVariation;
    const slidesArray = Array.isArray(variation?.slides) ? variation.slides : [];
    const approved = finalApproved || approvedVisuals;

    setIsFinalizing(true);

    // Create Snorkle library visuals for search-result items; library picks are already created
    const createdVisuals = await Promise.all(
      approved.map(item => {
        if (!item) return Promise.resolve(null);
        if (item.visualization_url) return Promise.resolve(item); // already a library visual
        return createVisualFromItem(item);
      })
    );

    // Add any newly created visuals to the global cache so they're available immediately
    // when the story is re-opened for editing from the library
    const newlyCachedVisuals = createdVisuals.filter(v => v && v.id);
    if (newlyCachedVisuals.length > 0) {
      setVisCache(prev => {
        const updated = { ...prev };
        newlyCachedVisuals.forEach(v => { updated[v.id] = v; });
        return updated;
      });
    }

    const draftSections = slidesArray.map((slide, index) => {
      const createdVis = createdVisuals[index];
      return {
        id: Date.now() + index,
        title: slide.section_title || `Slide ${slide.slide_number}`,
        text: (slide.text || '') + ' [Note: You may need to adapt this text to better match your selected visual]',
        visual: createdVis || null,
        visualUrl: createdVis?.visualization_url || '',
        suggestedVisual: slide.visual,
        isAccepted: false,
      };
    });

    const navigationState = {
      draftSections,
      draftMetadata: {
        title: variation.title || 'Untitled Story',
        abstract: variation.abstract || (variation.content?.substring(0, 200) + '...'),
        author: userSettings?.institutionProfile || '',
        date: new Date().toISOString().slice(0, 10)
      }
    };
    if (fromChannel) {
      navigationState.addToChannelOnSave = true;
      navigationState.channelEmail = userSettings?.channelEmail || '';
    }

    setIsFinalizing(false);
    setVisualPickingMode(false);
    navigate('/scrollytelling/builder', { state: navigationState });
    handleClose();
  };

  const hubCategories = [
    {
      id: 'science_comms',
      name: 'Interactive presentation content',
      icon: faChalkboard,
      description: 'Deck builder and interactive visuals for embedding in presentations',
      previewGif: '/snorkle_vis_thumbnails/slide_deck_sample.gif',
    },
    {
      id: 'educational',
      name: 'Science communication, educational, and outreach content',
      icon: faGraduationCap,
      description: 'Scrollytelling articles, quizzes, web and LMS embeds, social media ready content, DOIs',
      previewGif: '/snorkle_vis_thumbnails/scrolly_sample.gif',
      previewWidth: 180,
    },
    {
      id: 'anatomy',
      name: 'Custom 3D anatomy & animated explainers',
      icon: faBone,
      description: 'Interactive 3D anatomy explorers and narrated anatomical explainer videos',
      previewGif: '/snorkle_vis_thumbnails/anatomy_explorer_sample.gif',
    },
    {
      id: 'molecular',
      name: 'Molecular visualization and animation tools',
      icon: faDna,
      description: 'Chemistry / molecular scene builders (folding prediction and simulation tools coming soon)',
      previewGif: '/snorkle_vis_thumbnails/molecular_viewer_sample.gif',
    },
    {
      id: 'biomedical',
      name: 'Quickly share life sciences and clinical imagery (no PHI)',
      icon: faMicroscope,
      description: 'Life science and clinical imaging, 3D content, DOIs',
      previewGif: '/snorkle_vis_thumbnails/life_clin_combo_clip.gif',
    },
    {
      id: 'patient_education',
      name: 'Bedside patient education tools',
      icon: faHospital,
      description: 'Anatomy explorers, clinical imaging viewers, and 3D explainers for patient-facing education',
      previewGif: '/snorkle_vis_thumbnails/anatomy_explorer_sample.gif',
    },
    {
      id: 'dome_viz',
      name: 'Dome and large-format visualization tools',
      icon: faGlobe,
      description: 'Immersive biomedical visualization platform for planetariums, dome theaters, and large-screen installations',
    },
  ];

  const hubSubOptions = {
    science_comms: [
      { id: 'slide_deck', name: 'Go to deck builder', icon: faChalkboard, description: 'Google Slides type presentations where you can also embed interactive content.', typeId: 'slide_deck', premiumOnly: true },
      { id: 'standalone_visual', name: 'Interactive visuals for embedding in presentations', icon: faCubes, description: 'Browse or create your own, including models, imagery, chemistry, anatomy, and more.', typeId: 'standalone_visual' },
    ],
    educational: [
      { id: 'quiz', name: 'Quiz', icon: faClipboardList, description: 'Multiple choice quiz with an optional interactive visual background', typeId: 'quiz', premiumOnly: true, previewGif: '/snorkle_vis_thumbnails/quiz_sample_vid.gif', previewWidth: 180 },
      { id: 'scrollytelling_article', name: 'Scrolling article with interactive content', icon: faScroll, description: 'Scrollytelling formatted as an educational article', typeId: 'scrollytelling', premiumOnly: true, previewGif: '/snorkle_vis_thumbnails/scrolly_sample.gif', previewWidth: 180 },
      { id: 'web_lms_embed', name: 'Single interactive scenes for web & LMS embed', icon: faCode, description: 'Browse or create a standalone interactive visual for embedding in websites and LMS platforms', typeId: 'standalone_visual' },
    ],
    anatomy: [
      { id: 'anatomy_explorer', name: 'Custom 3D Anatomy Explorer', icon: faBone, description: 'Interactive anatomical exploration with custom labels and navigation', typeId: 'anatomy_explorer', premiumOnly: true, previewGif: '/snorkle_vis_thumbnails/anatomy_explorer_sample.gif' },
    ],
    molecular: [
      { id: 'chemistry_builder', name: 'Chemistry / molecular scene builder', icon: faFlask, description: 'Build interactive molecular scenes including basic PyMol interactivity', typeId: 'standalone_visual', visualTypeId: 'chemistry' },
      { id: 'folding_prediction', name: 'Folding prediction & simulation tools', icon: faDna, description: 'Protein folding prediction and molecular simulation', comingSoon: true },
    ],
    biomedical: [
      { id: 'life_sciences', name: 'Life science & medical imaging', icon: faMicroscope, description: 'Optical imaging, DICOM, pathology, and clinical imagery', typeId: 'standalone_visual', visualTypeId: 'life_sciences' },
      { id: 'biomedical_3d', name: '3D content', icon: faRoute, description: 'Interactive 3D biological and anatomical models', typeId: 'standalone_visual', visualTypeId: '3d_models' },
    ],
    patient_education: [
      { id: 'patient_anatomy_explorer', name: 'Custom 3D anatomy explorer', icon: faBone, description: 'Interactive 3D anatomical exploration for patient consultations and education', typeId: 'anatomy_explorer', premiumOnly: true, previewGif: '/snorkle_vis_thumbnails/anatomy_explorer_sample.gif' },
      { id: 'patient_imaging', name: 'Clinical imaging viewer', icon: faMicroscope, description: 'View and share de-identified clinical and life science imagery (not for PHI)', typeId: 'standalone_visual', visualTypeId: 'life_sciences', previewGif: '/snorkle_vis_thumbnails/clinical_imaging_snippet.gif' },
      { id: 'patient_anatomy_explainer', name: 'Animated 3D anatomical explainer videos', icon: faXRay, description: 'Fully-narrated, ontology-validated 3D medical explainer videos for patient education', typeId: 'tour', premiumOnly: true, accessCodeOnly: true, permission: 'create_explainers', previewGif: '/snorkle_vis_thumbnails/tour_snippet.gif' },
    ],
    dome_viz: [],
  };
  // Always show the explainer video option; free users see the premium upsell modal on click
  hubSubOptions.anatomy.push({ id: 'tour', name: 'Animated 3D anatomical explainer videos', icon: faXRay, description: 'Fully-narrated, ontology-validated 3D medical explainer videos', typeId: 'tour', premiumOnly: true, accessCodeOnly: true, permission: 'create_explainers', previewGif: '/snorkle_vis_thumbnails/tour_snippet.gif' });


  const contentTypes = [
    {
      id: 'standalone_visual',
      name: 'Interactive image or model',
      icon: faMicroscope,
      description: 'Models, videos, medical/life science imagery, chemistry/molecular, anatomy explorers, astronomy, and more',
      requiresAI: false,
      hasExisting: true // Always show manual option
    },
    {
      id: 'scrollytelling',
      name: 'Scrollytelling story',
      icon: faScroll,
      description: 'Scrolling scientific or medical articles with interactive content.',
      requiresAI: false,
      hasExisting: true // Always show manual option
    },
    {
      id: 'slide_deck',
      name: 'Slide deck',
      icon: faChalkboard,
      description: (
        <>
          PowerPoint-style presentations capable of interactive visuals. (
          <a href="/powerpoint" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            Also check out our free add-in for PowerPoint
          </a>
          )
        </>
      ),
      requiresAI: false,
      hasExisting: presentations?.length > 0
    },
    {
      id: 'quiz',
      name: 'Quiz',
      icon: faClipboardList,
      description: 'Multiple choice quiz with an optional interactive visual background',
      requiresAI: false,
      hasExisting: false
    },
    {
      id: 'anatomy_explorer',
      name: 'Custom 3D Anatomy Explorer',
      icon: faBone,
      description: 'Interactive anatomical exploration with custom labels, annotations, and navigation',
      requiresAI: false,
      hasExisting: false
    },
    {
      id: 'tour',
      name: 'Animated 3D anatomical explainer videos',
      icon: faXRay,
      description: 'Fully-narrated, ontology-validated, 3D medical explainer videos ',
      requiresAI: false,
      hasExisting: explainers?.length > 0
    }
  ];

  const resetQuizState = () => {
    setQuizTitle('');
    setQuizQuestion('');
    setQuizAnswers([
      { id: `ans-${Date.now()}-0`, text: '' },
      { id: `ans-${Date.now()}-1`, text: '' },
    ]);
    setQuizCorrectIndex(0);
    setQuizVisual(null);
    setShowQuizVisualPicker(false);
    setIsSavingQuiz(false);
    setQuizSaveError(null);
  };

  // Lock body scroll while saving quiz
  useEffect(() => {
    if (isSavingQuiz) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isSavingQuiz]);

  const handleClose = () => {
    console.log('🚪 Wizard closing, resetting all state');
    setStep(1);
    setSelectedHub(null);
    setSelectedContentType(null);
    setCreationMethod(null);
    setHasOwnResources(null);
    setManualSourceType(null);
    setRequestDOI(false);
    setVisualType(null);
    setAISearchTerm('');
    setSearchResults([]);
    setCreatedVisual(null);
    setViewingExamples(false);
    setViewingVisualTypeExamples(false);
    resetQuizState();
    setVisualPickingMode(false);
    setPendingVariation(null);
    setApprovedVisuals([]);
    setSlideSearchResults([]);
    setVisualPickingIdx(0);
    setIsFinalizing(false);
    setShowAllPickerResults(false);
    setShowSlideVisualPicker(false);
    onHide();
  };

  const handleBackToStep1 = () => {
    console.log('⬅️ Going back to Step 1 (hub selection), resetting all state');
    setStep(1);
    setSelectedHub(null);
    setSelectedContentType(null);
    setCreationMethod(null);
    setHasOwnResources(null);
    setManualSourceType(null);
    setRequestDOI(false);
    setVisualType(null);
    setAISearchTerm('');
    setSearchResults([]);
    setCreatedVisual(null);
    setViewingExamples(false);
    setViewingVisualTypeExamples(false);
    resetQuizState();
    setVisualPickingMode(false);
    setPendingVariation(null);
    setApprovedVisuals([]);
    setSlideSearchResults([]);
    setVisualPickingIdx(0);
    setShowAllPickerResults(false);
    setShowSlideVisualPicker(false);
  };

  const handleBackToHub = () => {
    // Biomedical hub skips step 1.5, so back should return to step 1
    if (selectedHub === 'biomedical') {
      handleBackToStep1();
      return;
    }
    console.log('⬅️ Going back to hub sub-options (step 1.5)');
    setStep(1.5);
    setSelectedContentType(null);
    setCreationMethod(null);
    setHasOwnResources(null);
    setManualSourceType(null);
    setRequestDOI(false);
    setVisualType(null);
    setAISearchTerm('');
    setSearchResults([]);
    setCreatedVisual(null);
    setViewingExamples(false);
    setViewingVisualTypeExamples(false);
    resetQuizState();
  };

  const handleSelectSubOption = (subOption) => {
    if (subOption.comingSoon) return;
    if (subOption.premiumOnly && isFreeUser) {
      setPremiumFeatureName(subOption.name || 'This feature');
      setShowPremiumModal(true);
      return;
    }
    if (subOption.accessCodeOnly) {
      setAccessCodeFeatureName(subOption.name || 'This feature');
      setAccessCodePermission(subOption.permission || '');
      setAccessCodeAction(() => () => handleSelectSubOption({ ...subOption, accessCodeOnly: false }));
      setShowAccessCodeModal(true);
      return;
    }
    if (subOption.hubId) {
      // Navigate to another hub's sub-options
      setSelectedHub(subOption.hubId);
      setStep(1.5);
    } else if (subOption.typeId && subOption.visualTypeId) {
      // Pre-select content type and visual subtype
      if (subOption.visualTypeId === 'videos_images') {
        // Traditional images/videos: skip Yes/No and source type, go straight to DOI
        setSelectedContentType(subOption.typeId);
        setVisualType(subOption.visualTypeId);
        setCreationMethod('manual');
        setHasOwnResources(true);
        setManualSourceType('file');
        setStep(5);
      } else {
        // Skip step 2.5 visual type picker
        setSelectedContentType(subOption.typeId);
        setVisualType(subOption.visualTypeId);
        setCreationMethod('manual');
        setStep(3);
      }
    } else if (subOption.typeId) {
      handleSelectContentType(subOption.typeId);
    }
  };

  const handleSelectContentType = (typeId) => {
    console.log('📝 Content type selected:', typeId);
    setSelectedContentType(typeId);

    // Slide deck goes directly to presentation builder (no AI/Manual choice)
    if (typeId === 'slide_deck') {
      console.log('🎬 Navigating to presentation builder');
      navigate('/create-presentation');
      handleClose();
      return;
    }

    // Standalone visual goes to visual type selection (step 2.5)
    if (typeId === 'standalone_visual') {
      console.log('🖼️ Standalone visual selected, going to visual type selection (step 2.5)');
      setCreationMethod('manual'); // Set this so subsequent steps work
      setStep(2.5);
      return;
    }

    // Animated explainer videos go directly to the explainer builder page
    if (typeId === 'tour') {
      console.log('🎥 Navigating to explainer builder');
      navigate('/explainer');
      handleClose();
      return;
    }

    // Anatomy explorer goes directly to its dedicated search step
    if (typeId === 'anatomy_explorer') {
      console.log('🦴 Anatomy explorer selected, going to anatomy search step');
      setStep('anatomy_search');
      return;
    }

    // Quiz goes directly to the quiz builder (step 'quiz')
    if (typeId === 'quiz') {
      console.log('📋 Quiz selected, going to quiz builder');
      setStep('quiz');
      return;
    }

    console.log('➡️ Proceeding to step 2 (AI/Manual choice)');
    setStep(2);
  };

  const handleSelectMethod = (method) => {
    console.log('🤖 Creation method selected:', method, 'for content type:', selectedContentType);
    setCreationMethod(method);

    const selectedType = contentTypes.find(t => t.id === selectedContentType);

    if (method === 'manual') {
      // Handle manual selection
      if (selectedContentType === 'standalone_visual') {
        console.log('📦 Manual standalone - going to resource availability (step 3)');
        // Go to resource availability question
        setStep(3);
      } else if (selectedContentType === 'scrollytelling') {
        console.log('📖 Manual scrollytelling - going to DOI question (step 8)');
        // Ask about DOI for scrollytelling
        setStep(8);
      } else {
        console.log('📂 Manual other type - going to content picker (step 4)');
        // For other types, show existing content picker
        setStep(4); // Manual picker step
      }
    } else {
      // AI generation - also ask about DOI for scrollytelling
      if (selectedContentType === 'scrollytelling') {
        console.log('📖 AI scrollytelling - going to DOI question (step 8)');
        setStep(8); // DOI info step before AI config
      } else {
        console.log('🤖 AI generation - going to config (step 3)');
        setStep(3); // AI config step
      }
    }
  };

  const handleGenerate = () => {
    onGenerate(selectedContentType);
  };

  const handleDraftScrollytellingFromPdf = () => {
    navigate('/scrollytelling/builder', { state: { openAiDraftFromPdf: true } });
    handleClose();
  };

  const handleSaveQuiz = () => {
    if (!quizQuestion.trim()) {
      setQuizSaveError('Please enter a quiz question.');
      return;
    }
    if (quizAnswers.some(a => !a.text.trim())) {
      setQuizSaveError('Please fill in all answer options.');
      return;
    }
    setQuizSaveError(null);
    setIsSavingQuiz(true);

    const payload = {
      title: quizTitle.trim() || 'Untitled Quiz',
      description: quizQuestion.trim(),
      is_presentation: true,
      is_quiz: true,
      type: 'scrollytelling',
      metadata: {
        title: quizTitle.trim() || 'Untitled Quiz',
        abstract: quizQuestion.trim(),
        author: userInfo?.display_name || '',
        date: new Date().toISOString().slice(0, 10),
      },
      slides: [
        {
          type: 'quiz',
          visual_id: quizVisual ? quizVisual.id : undefined,
          header: quizTitle.trim() || undefined,
          question: quizQuestion.trim(),
          answers: quizAnswers,
          correctAnswerIndex: quizCorrectIndex,
        },
      ],
      privacy: 'unlisted',
    };

    console.log('[ContentCreationWizard] Saving quiz payload:', JSON.stringify(payload, null, 2));

    APIAddEditPresentation(
      payload,
      (savedStory) => {
        console.log('[ContentCreationWizard] Quiz saved successfully:', JSON.stringify(savedStory, null, 2));
        setIsSavingQuiz(false);
        const visCacheCopy = shallowCopy(visCache);
        visCacheCopy[savedStory.id] = savedStory;
        setVisCache(visCacheCopy);
        setCreatedVisual(savedStory);
        setStep(9);
      },
      (err) => {
        console.error('[ContentCreationWizard] Quiz save failed:', err);
        setIsSavingQuiz(false);
        setQuizSaveError(err?.message || 'Failed to save quiz. Please try again.');
      },
      false,      // isOrg
      'scrollytelling'
    );
  };

  const selectedType = contentTypes.find(t => t.id === selectedContentType);

  // Calculate progress stages dynamically based on content type and method
  // ALWAYS show exactly 4 stages: Type → [unlabeled] → [unlabeled] → Complete
  const getProgressStages = () => {
    // Success screen (step 9) - show all complete
    if (step === 9 && createdVisual) {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: 'Complete', active: true, complete: false }
      ];
    }
    
    // Default/Initial or hub sub-selection: Show generic 4-stage timeline with Type active
    if (!selectedContentType || step === 1.5) {
      return [
        { label: 'Type', active: true, complete: false },
        { label: '', active: false, complete: false },
        { label: '', active: false, complete: false },
        { label: 'Complete', active: false, complete: false }
      ];
    }

    // Slide Deck - direct navigation
    if (selectedContentType === 'slide_deck') {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: true, complete: false },
        { label: '', active: false, complete: false },
        { label: 'Complete', active: false, complete: false }
      ];
    }

    // Quiz: Type(✓) → Build(step 'quiz') → Complete(step 9)
    if (selectedContentType === 'quiz') {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: step === 'quiz', complete: step === 9 },
        { label: '', active: false, complete: false },
        { label: 'Complete', active: step === 9, complete: false }
      ];
    }

    // Standalone Visual - ALWAYS 4 stages
    if (selectedContentType === 'standalone_visual') {
      if (hasOwnResources === null) {
        // Choosing source (step 3)
        return [
          { label: 'Type', active: false, complete: true },
          { label: '', active: step === 3, complete: false },
          { label: '', active: false, complete: false },
          { label: 'Complete', active: false, complete: false }
        ];
      } else if (hasOwnResources === false) {
        // Search path
        if (creationMethod === 'ai') {
          // AI search: Type(✓) → Source(✓) → Configure(step7) → Results(step5)
          return [
            { label: 'Type', active: false, complete: true },
            { label: '', active: false, complete: true },
            { label: '', active: step === 7, complete: (generatedContent && step === 5) },
            { label: 'Complete', active: (generatedContent && step === 5), complete: false }
          ];
        } else {
          // Manual search: Type(✓) → Source(✓) → Search(step3) → Select
          return [
            { label: 'Type', active: false, complete: true },
            { label: '', active: false, complete: true },
            { label: '', active: step === 3, complete: false },
            { label: 'Complete', active: false, complete: false }
          ];
        }
      } else {
        // Upload path: Type(✓) → Source(✓) → Details(step4&5) → Upload(step6)
        return [
          { label: 'Type', active: false, complete: true },
          { label: '', active: false, complete: true },
          { label: '', active: (step === 4 || step === 5), complete: step >= 6 },
          { label: 'Complete', active: step === 6, complete: false }
        ];
      }
    }

    if (selectedContentType === 'anatomy_explorer') {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: 'Complete', active: true, complete: false }
      ];
    }

    // After selecting other content types, at step 2 (choosing method)
    if (step === 2 && !creationMethod) {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: true, complete: false },
        { label: '', active: false, complete: false },
        { label: 'Complete', active: false, complete: false }
      ];
    }

    // Scrollytelling Manual: Type(✓) → Method(✓) → Details(step8) → Create
    if (selectedContentType === 'scrollytelling' && creationMethod === 'manual') {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: '', active: step === 8, complete: false },
        { label: 'Complete', active: false, complete: false }
      ];
    }

    // Scrollytelling/Quiz AI: Type(✓) → Method(✓) → Configure(step3 or 8) → Review(step5)
    if ((selectedContentType === 'scrollytelling' || selectedContentType === 'quiz') && creationMethod === 'ai') {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: '', active: (step === 3 || step === 8), complete: (step === 5 && generatedContent) },
        { label: 'Complete', active: (step === 5 && generatedContent), complete: false }
      ];
    }

    // Tour Manual: Type(✓) → Method(✓) → Select(step4) → Complete
    if (selectedContentType === 'tour' && creationMethod === 'manual') {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: '', active: step === 4, complete: false },
        { label: 'Complete', active: false, complete: false }
      ];
    }

    // Tour AI: Type(✓) → Method(✓) → Configure(step3) → Review(step5)
    if (selectedContentType === 'tour' && creationMethod === 'ai') {
      return [
        { label: 'Type', active: false, complete: true },
        { label: '', active: false, complete: true },
        { label: '', active: step === 3, complete: (step === 5 && generatedContent) },
        { label: 'Complete', active: (step === 5 && generatedContent), complete: false }
      ];
    }

    // Default fallback - ALWAYS 4 stages
    return [
      { label: 'Type', active: step === 1, complete: step > 1 },
      { label: '', active: step === 2, complete: step > 2 },
      { label: '', active: step >= 3 && step < 5, complete: step >= 5 },
      { label: 'Complete', active: step >= 5, complete: false }
    ];
  };

  const progressStages = getProgressStages();

  // Log which step is being rendered
  React.useEffect(() => {
    const conditions = {
      'Step 1 (Hub Selection)': step === 1,
      'Step 1.5 (Hub Sub-Options)': step === 1.5 && selectedHub && !viewingExamples,
      'Step 1.5 (Examples)': step === 1.5 && viewingExamples,
      'Step 2 (AI/Manual Choice)': step === 2 && selectedType,
      'Step 2.5 (Visual Type)': step === 2.5 && selectedContentType === 'standalone_visual',
      'Step 3 (Standalone Resource Question)': step === 3 && selectedContentType === 'standalone_visual' && hasOwnResources === null,
      'Step 3 (Standalone Search)': step === 3 && selectedContentType === 'standalone_visual' && hasOwnResources === false,
      'Step 3 (AI Config)': step === 3 && creationMethod === 'ai' && selectedContentType !== 'standalone_visual',
      'Step 4 (Source Type)': step === 4 && selectedContentType === 'standalone_visual' && hasOwnResources === true,
      'Step 4 (Manual Picker)': step === 4 && creationMethod === 'manual' && selectedContentType !== 'standalone_visual',
      'Step 5 (DOI Question)': step === 5 && selectedContentType === 'standalone_visual' && hasOwnResources === true && manualSourceType === 'file',
      'Step 5.5 (DOI Instructions)': step === 5.5 && selectedContentType === 'standalone_visual' && hasOwnResources === true && manualSourceType === 'file' && requestDOI === true,
      'Step 5 (Results)': step === 5 && generatedContent,
      'Step 6 (Upload)': step === 6 && selectedContentType === 'standalone_visual' && hasOwnResources === true,
      'Step 7 (AI Visual Search Config)': step === 7 && selectedContentType === 'standalone_visual' && creationMethod === 'ai',
      'Step 8 (Scrollytelling DOI)': step === 8 && selectedContentType === 'scrollytelling',
      'Step quiz (Quiz Builder)': step === 'quiz' && selectedContentType === 'quiz'
    };

    const activeConditions = Object.entries(conditions).filter(([_, value]) => value).map(([key]) => key);

    if (activeConditions.length === 0) {
      console.error('❌ NO STEP CONDITIONS MET! This will show a blank screen!', {
        step,
        selectedContentType,
        creationMethod,
        hasOwnResources,
        manualSourceType,
        requestDOI,
        generatedContent: !!generatedContent
      });
    } else {
      console.log('✅ Rendering:', activeConditions.join(', '));
    }
  }, [step, selectedHub, selectedContentType, creationMethod, hasOwnResources, manualSourceType, generatedContent, selectedType, viewingExamples, requestDOI]);

  return (
    <>
    <Modal show={show} onHide={handleClose} size="xl" centered dialogClassName="wizard-modal" enforceFocus={!showAccessCodeModal && !showPremiumModal}>
      {isGenerating && (
        <WizardGeneratingOverlay />
      )}
      <Modal.Header closeButton>
        <Modal.Title>Create Content</Modal.Title>
      </Modal.Header>

      <Modal.Body className="wizard-body">
        {/* Progress Indicator */}
        {progressStages.length > 0 && (
          <div className="wizard-progress-container">
            <div className="wizard-progress-bar">
              {progressStages.filter(stage => !stage.hidden).map((stage, index) => (
                <div key={index} className={`wizard-progress-stage ${stage.placeholder ? 'placeholder' : ''}`}>
                  <div className={`wizard-progress-step ${stage.active ? 'active' : ''} ${stage.complete ? 'complete' : ''} ${stage.placeholder ? 'placeholder' : ''}`}>
                    {stage.placeholder ? (
                      <span>...</span>
                    ) : stage.complete ? (
                      <FontAwesomeIcon icon={faCheckCircle} />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="wizard-progress-label">{stage.label}</div>
                  {index < progressStages.filter(s => !s.hidden).length - 1 && (
                    <div className={`wizard-progress-line ${stage.complete ? 'complete' : ''}`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Choose Hub Category */}
        {step === 1 && (
          <div className="wizard-step">
            <h5 className="wizard-step-title">What would you like to create?</h5>
            <div className="content-type-grid hub-grid">
              {hubCategories.map((hub) => (
                <div
                  key={hub.id}
                  className={`content-type-card hub-card${hub.comingSoon ? ' hub-card-coming-soon' : ''}${hub.premiumOnly && isFreeUser ? ' hub-card-premium' : ''}`}
                  onClick={() => {
                    if (hub.comingSoon) return;
                    if (hub.premiumOnly && isFreeUser) {
                      setPremiumFeatureName(hub.name || 'This feature');
                      setShowPremiumModal(true);
                      return;
                    }
                    setSelectedHub(hub.id);
                    if (hub.id === 'biomedical') {
                      // Go directly to life sciences resource question
                      setSelectedContentType('standalone_visual');
                      setVisualType('life_sciences');
                      setCreationMethod('manual');
                      setStep(3);
                    } else {
                      setStep(1.5);
                    }
                  }}
                  style={{ cursor: hub.comingSoon ? 'default' : 'pointer' }}
                  onMouseEnter={hub.previewGif ? (e) => {
                    clearTimeout(gifPreviewTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    gifPreviewTimerRef.current = setTimeout(() => {
                      setHoveredHubPreview({ gif: hub.previewGif, rect, width: hub.previewWidth });
                    }, 300);
                  } : undefined}
                  onMouseLeave={hub.previewGif ? () => {
                    clearTimeout(gifPreviewTimerRef.current);
                    setHoveredHubPreview(null);
                  } : undefined}
                >
                  <div className="content-type-icon">
                    <FontAwesomeIcon icon={hub.icon} />
                  </div>
                  {hub.premiumOnly && isFreeUser && (
                    <span className="premium-ribbon">
                      <FontAwesomeIcon icon={faStar} /> Premium
                    </span>
                  )}
                  <h5>{hub.name}</h5>
                  <p>{hub.description}</p>
                  {hub.comingSoon && (
                    <span className="coming-soon-badge">Coming soon</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1.5: Hub Sub-Options */}
        {step === 1.5 && selectedHub && !viewingExamples && (
          <div className="wizard-step">
            <Button variant="link" onClick={handleBackToStep1}>← Back</Button>
            <h5 className="wizard-step-title">
              {hubCategories.find(h => h.id === selectedHub)?.name}
            </h5>

            {/* Dome visualization hub — info panel */}
            {selectedHub === 'dome_viz' && (
              <div className="wizard-notice wizard-notice-info">
                <FontAwesomeIcon icon={faGlobe} className="wizard-notice-icon" />
                <div>
                  <strong>Dome & large-format visualization</strong>
                  <p className="mb-0 mt-1">All interactive visuals can support live, immersive visualization in dome theaters, planetariums, and large-screen installations. <a href="/contact" rel="noopener noreferrer">Contact us</a> for help activating this feature.</p>
                </div>
              </div>
            )}

            {/* Molecular hub note */}
            {selectedHub === 'molecular' && (
              <div className="wizard-notice wizard-notice-info mb-3">
                <FontAwesomeIcon icon={faAtom} className="wizard-notice-icon" />
                <div>
                  <strong>All visual output can be embedded in articles, presentations, and websites.</strong>
                </div>
              </div>
            )}

            {/* Sub-options grid */}
            {(hubSubOptions[selectedHub] || []).length > 0 && (
              <div className="content-type-grid">
                {(hubSubOptions[selectedHub] || []).map((sub) => (
                  <div
                    key={sub.id}
                    className={`content-type-card${sub.comingSoon ? ' hub-card-coming-soon' : ''}${sub.premiumOnly && isFreeUser ? ' hub-card-premium' : ''}`}
                    onClick={() => handleSelectSubOption(sub)}
                    style={{ cursor: sub.comingSoon ? 'default' : 'pointer' }}
                    onMouseEnter={sub.previewGif ? (e) => {
                      clearTimeout(gifPreviewTimerRef.current);
                      const rect = e.currentTarget.getBoundingClientRect();
                      gifPreviewTimerRef.current = setTimeout(() => {
                        setHoveredHubPreview({ gif: sub.previewGif, rect, width: sub.previewWidth });
                      }, 300);
                    } : undefined}
                    onMouseLeave={sub.previewGif ? () => {
                      clearTimeout(gifPreviewTimerRef.current);
                      setHoveredHubPreview(null);
                    } : undefined}
                  >
                    {sub.premiumOnly && isFreeUser && (
                      <span className="premium-ribbon">
                        <FontAwesomeIcon icon={faStar} /> Premium
                      </span>
                    )}
                    <div className="content-type-icon">
                      <FontAwesomeIcon icon={sub.icon} />
                    </div>
                    <h5>{sub.name}</h5>
                    <p>{sub.description}</p>
                    {sub.comingSoon && (
                      <span className="coming-soon-badge">Coming soon</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 1.5: View Examples (from sub-option cards) */}
        {step === 1.5 && viewingExamples && selectedContentType && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              setViewingExamples(false);
              setSelectedContentType(null);
            }}>← Back to Options</Button>
            <h5 className="wizard-step-title">Examples: {selectedType?.name}</h5>
            <p className="text-muted mb-3">{selectedType?.description}</p>

            <div className="examples-grid mb-4">
              {selectedContentType === 'standalone_visual' && visuals && visuals.slice(0, 3).map((vis) => (
                <CarouselVisCard
                  key={vis.id}
                  vis={vis}
                  blockActions={true}
                  isOrg={false}
                  canEdit={false}
                  canAddToChannel={false}
                  showModal={false}
                />
              ))}

              {selectedContentType === 'presentation' && presentations && presentations.slice(0, 3).map((pres) => (
                <CarouselPresentationCard
                  key={pres.id}
                  presentation={pres}
                  canEdit={false}
                />
              ))}

              {selectedContentType === 'tour' && explainers && explainers.slice(0, 3).map((exp) => (
                <CarouselExplainerCard
                  key={exp.id}
                  explainer={exp}
                  canEdit={false}
                />
              ))}

              {/* Show placeholder if no examples */}
              {((selectedContentType === 'standalone_visual' && (!visuals || visuals.length === 0)) ||
                (selectedContentType === 'presentation' && (!presentations || presentations.length === 0)) ||
                (selectedContentType === 'tour' && (!explainers || explainers.length === 0)) ||
                selectedContentType === 'scrollytelling' ||
                selectedContentType === 'quiz' ||
                selectedContentType === 'slide_deck') && (
                  <div className="text-center text-muted">
                    <p>No examples available yet. This will show sample {selectedType?.name} content.</p>
                  </div>
                )}
            </div>

            <div className="wizard-cta-row">
              <Button
                variant="primary"
                onClick={() => {
                  setViewingExamples(false);
                  handleSelectContentType(selectedContentType);
                }}
              >
                Create {selectedType?.name}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Choose Creation Method */}
        {step === 2 && selectedType && (
          <div className="wizard-step">
            <Button variant="link" onClick={handleBackToHub}>← Back</Button>
            <h5 className="wizard-step-title">How do you want to create this content?</h5>

            <div className="creation-method-grid">
              <div
                className="creation-method-card"
                onClick={() => handleSelectMethod('ai')}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faRobot} />
                </div>
                <h5>Get AI Suggestions</h5>
                <p>Generate content ideas based on your mission, audience, and research</p>
                <div className="method-features">
                  <div>✓ Research-backed content</div>
                  <div>✓ Customized to your mission</div>
                  <div>✓ Multiple variations</div>
                </div>
              </div>

              {selectedType.hasExisting && (
                <div
                  className="creation-method-card"
                  onClick={() => handleSelectMethod('manual')}
                >
                  <div className="method-icon">
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </div>
                  <h5>Add Manually</h5>
                  <p>Select from your existing {selectedType.name.toLowerCase()} library</p>
                  <div className="method-features">
                    <div>✓ Use existing content</div>
                    <div>✓ Quick selection</div>
                    <div>✓ {selectedType.hasExisting ? 'Content available' : 'No content yet'}</div>
                  </div>
                </div>
              )}

              {!selectedType.hasExisting && selectedContentType !== 'scrollytelling' && (
                <div className="creation-method-card disabled">
                  <div className="method-icon">
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </div>
                  <h5>Add Manually</h5>
                  <p>No existing {selectedType.name.toLowerCase()} in your library yet</p>
                  <div className="method-note">
                    Create content first, then add it to your channel
                  </div>
                </div>
              )}

              {selectedType.requiresAI && (
                <div className="creation-method-card disabled">
                  <div className="method-icon">
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </div>
                  <h5>Add Manually</h5>
                  <p>Scrollytelling stories must be generated with AI</p>
                  <div className="method-note">
                    Use AI suggestions to create research-based narratives
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2.5: Visual Type Selection (for standalone visual) */}
        {step === 2.5 && selectedContentType === 'standalone_visual' && !viewingVisualTypeExamples && (
          <div className="wizard-step">
            <Button variant="link" onClick={handleBackToHub}>← Back</Button>
            <h5 className="wizard-step-title">Type of interactive visual</h5>

            <div className="creation-method-grid">
              {[
                { id: 'life_sciences', icon: faImage, label: 'Life Sciences or Medical Imaging', desc: 'Optical imaging, DICOM, pathology, and more' },
                { id: '3d_models', icon: faRoute, label: '3D Models (with optional narration)', desc: 'Interactive 3D objects and scenes' },
                { id: 'chemistry', icon: faImage, label: 'Chemistry / Molecular Scenes', desc: 'Molecular structures, chemical bonds, reactions' },
                { id: 'videos_images', icon: faPlay, label: 'Upload Traditional Images or Videos', desc: 'Video files, photos, static images' },
              ].map(({ id, icon, label, desc }) => (
                <div key={id} className="creation-method-card" onClick={() => {
                  setVisualType(id);
                  if (id === 'videos_images') {
                    setHasOwnResources(true);
                    setManualSourceType('file');
                    setStep(5);
                  } else {
                    setStep(3);
                  }
                }}>
                  <div className="method-icon">
                    <FontAwesomeIcon icon={icon} />
                  </div>
                  <h5>{label}</h5>
                  <p>{desc}</p>
                </div>
              ))}

              {/* Custom 3D Anatomy Explorer — premium gated */}
              <div
                className={`creation-method-card${isFreeUser ? ' hub-card-premium' : ''}`}
                onClick={() => {
                  if (isFreeUser) {
                    setPremiumFeatureName('Custom 3D Anatomy Explorer');
                    setShowPremiumModal(true);
                  } else {
                    setSelectedContentType('anatomy_explorer');
                    setStep('anatomy_search');
                  }
                }}
                onMouseEnter={(e) => {
                  clearTimeout(gifPreviewTimerRef.current);
                  const rect = e.currentTarget.getBoundingClientRect();
                  gifPreviewTimerRef.current = setTimeout(() => {
                    setHoveredHubPreview({ gif: '/snorkle_vis_thumbnails/anatomy_explorer_sample.gif', rect });
                  }, 300);
                }}
                onMouseLeave={() => {
                  clearTimeout(gifPreviewTimerRef.current);
                  setHoveredHubPreview(null);
                }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faBone} />
                </div>
                <h5>
                  Custom 3D Anatomy Explorer
                  {isFreeUser && (
                    <span className="premium-ribbon ms-2">
                      <FontAwesomeIcon icon={faStar} /> Premium
                    </span>
                  )}
                </h5>
                <p>Interactive anatomical exploration with custom labels and navigation</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2.5b: Visual Type Examples */}
        {step === 2.5 && selectedContentType === 'standalone_visual' && viewingVisualTypeExamples && visualType && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              setViewingVisualTypeExamples(false);
              setVisualType(null);
            }}>← Back to Visual Types</Button>
            <h5 className="wizard-step-title">
              Examples: {
                visualType === 'life_sciences' ? 'Life Sciences or Medical Imagery' :
                  visualType === '3d_models' ? '3D Models' :
                    visualType === 'chemistry' ? 'Chemistry / Molecular Scenes' :
                      visualType === 'videos_images' ? 'Videos or Traditional Images' : ''
              }
            </h5>

            <div className="examples-grid mb-4">
              {/* Filter visuals by type if possible, or show general examples */}
              {visuals && visuals.slice(0, 3).map((vis) => (
                <CarouselVisCard
                  key={vis.id}
                  vis={vis}
                  blockActions={true}
                  isOrg={false}
                  canEdit={false}
                  canAddToChannel={false}
                  showModal={false}
                />
              ))}

              {(!visuals || visuals.length === 0) && (
                <div className="text-center text-muted">
                  <p>No examples available yet. This will show sample visuals for this category.</p>
                </div>
              )}
            </div>

            <div className="wizard-cta-row">
              <Button
                variant="primary"
                onClick={() => {
                  setViewingVisualTypeExamples(false);
                  setStep(3);
                }}
              >
                Create This Type
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Chemistry Source Picker — skips Yes/No and shows all options at once */}
        {step === 3 && selectedContentType === 'standalone_visual' && visualType === 'chemistry' && hasOwnResources === null && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              setVisualType(null);
              setSelectedContentType(null);
              setCreationMethod(null);
              // If creationMethod was pre-set (came from hub card or sub-option), go back to hub
              // If it was null (came through step 2.5), go back to step 2.5
              if (creationMethod === 'manual') handleBackToHub();
              else setStep(2.5);
            }}>← Back</Button>
            <h5 className="wizard-step-title">How would you like to create your molecular scene?</h5>

            <div className="creation-method-grid">
              {/* Upload molecular files */}
              <div
                className="creation-method-card"
                onClick={() => { setHasOwnResources(true); setManualSourceType('file'); setStep(5); }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faFlask} />
                </div>
                <h5>Upload molecular scene files</h5>
                <div className="method-features">
                  <div>✓ .pdb, .sdf, .cif, and .mol2</div>
                  <div>✓ PyMol scenes exported as COLLADA (.dae)</div>
                </div>
              </div>

              {/* Web linked resources */}
              <div
                className="creation-method-card"
                onClick={() => { setHasOwnResources(true); setManualSourceType('web'); setRequestDOI(false); setStep(6); }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faRoute} />
                </div>
                <h5>Web linked resources</h5>
                <p>Link to molecules via PubChem, PDB IDs, or other web sources</p>
              </div>

              {/* Live molecular viewer — premium gated */}
              <div
                className={`creation-method-card${isFreeUser ? ' hub-card-premium' : ''}`}
                onClick={() => {
                  if (isFreeUser) {
                    setPremiumFeatureName("Snorkle's Live Molecular Viewer");
                    setShowPremiumModal(true);
                  } else {
                    navigate('/molecular-viewer');
                    handleClose();
                  }
                }}
                onMouseEnter={(e) => {
                  clearTimeout(gifPreviewTimerRef.current);
                  const rect = e.currentTarget.getBoundingClientRect();
                  gifPreviewTimerRef.current = setTimeout(() => {
                    setHoveredHubPreview({ gif: '/snorkle_vis_thumbnails/molecular_viewer_sample.gif', rect });
                  }, 300);
                }}
                onMouseLeave={() => {
                  clearTimeout(gifPreviewTimerRef.current);
                  setHoveredHubPreview(null);
                }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faAtom} />
                </div>
                <h5>
                  Create using Snorkle's live viewer
                  {isFreeUser && (
                    <span className="premium-ribbon ms-2">
                      <FontAwesomeIcon icon={faStar} /> Premium
                    </span>
                  )}
                </h5>
                <p>Build and save a molecular scene interactively</p>
                <div className="mt-auto pt-2" onClick={(e) => e.stopPropagation()}>
                  <YouTubeVideoPopup
                    videoUrl="https://youtu.be/hfY2y3XGLmI"
                    linkText="Watch tutorial"
                  />
                </div>
              </div>

              {/* Search / AI brainstorm */}
              <div
                className="creation-method-card"
                onClick={() => setHasOwnResources(false)}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faMagic} />
                </div>
                <h5>No, help me create or search</h5>
                <p>Search Snorkle's visual database for existing content</p>
                <div className="method-features">
                  <div>✓ Search existing visuals</div>
                  <div>✓ Browse by topic</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Resource Availability Question (Standalone Visual — non-chemistry) */}
        {step === 3 && selectedContentType === 'standalone_visual' && visualType !== 'chemistry' && hasOwnResources === null && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              setVisualType(null);
              setSelectedContentType(null);
              setCreationMethod(null);
              // If creationMethod was pre-set (came from hub card or sub-option), go back to hub
              if (creationMethod === 'manual') handleBackToHub();
              else setStep(2.5);
            }}>← Back</Button>
            <h5 className="wizard-step-title">Do you have your own resources to upload?</h5>

            <div className="creation-method-grid">
              <div
                className="creation-method-card"
                onClick={() => {
                  setHasOwnResources(true);
                  // Life sciences uploads get a PHI warning first
                  setStep(visualType === 'life_sciences' ? 'phi_warning' : 4);
                }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <h5>Yes, I have resources</h5>
                <p>
                  {visualType === 'life_sciences'
                    ? 'Upload image stacks, DICOM files, multi-channel images, zipped image folders, or web links'
                    : 'Upload your own images, 3D models, videos, or web links'}
                </p>
                <div className="method-features">
                  <div>✓ Images, Models, Videos</div>
                  <div>✓ Web Resources</div>
                  <div>✓ Request DOI option</div>
                </div>
              </div>

              <div
                className="creation-method-card"
                onClick={() => { setHasOwnResources(false); }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faMagic} />
                </div>
                <h5>
                  {visualType === 'life_sciences' ? 'No, help me find imagery' :
                    visualType === '3d_models' ? 'No, help me find 3D models' :
                      'No, help me create or search'}
                </h5>
                <p>Search Snorkle's visual database for existing content</p>
                <div className="method-features">
                  <div>✓ Search existing visuals</div>
                  <div>✓ Browse by topic</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PHI Warning step — shown for life sciences uploads before source type selection */}
        {step === 'phi_warning' && selectedContentType === 'standalone_visual' && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => { setHasOwnResources(null); setStep(3); }}>← Back</Button>
            <h5 className="wizard-step-title">Important Notice Before Uploading</h5>

            <div className="wizard-notice wizard-notice-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} className="wizard-notice-icon" />
              <div>
                <strong>Do not upload content containing Protected Health Information (PHI).</strong>
                <p className="mb-0 mt-2">
                  Snorkle is <strong>not intended for clinical use</strong> and file handling is currently not HIPAA-compliant. Please ensure all images and files have been fully de-identified before uploading. Do not upload patient data, medical records, or any identifiable information.
                </p>
              </div>
            </div>

            <div className="wizard-cta-row">
              <Button variant="primary" onClick={() => {
                // Life sciences skips the source type picker — file upload only
                setManualSourceType('file');
                setStep(5);
              }}>
                I understand — Continue to Upload
              </Button>
            </div>
          </div>
        )}

        {/* Anatomy Explorer search step */}
        {step === 'anatomy_search' && selectedContentType === 'anatomy_explorer' && (
          <div className="wizard-step">
            <Button variant="link" onClick={handleBackToHub}>← Back</Button>
            <h5 className="wizard-step-title">Configure your anatomy explorer</h5>
            <p className="text-muted mb-4">Create an interactive 3D anatomy explorer scene in seconds.</p>

            <div className="wizard-upload-section">
              {anatomyCreationError && (
                <div className="wizard-search-error">
                  <p className="text-danger">{anatomyCreationError}</p>
                </div>
              )}
              <div className="wizard-search-row">
                <Typeahead
                  options={anatomySuggestions}
                  style={{ width: '100%' }}
                  labelKey="name"
                  valueKey="entry_key"
                  minLength={2}
                  disabled={isCreatingAnatomy}
                  placeholder="Search anatomical structures, regions, systems…"
                  onChange={(selected) => {
                    setSelectedAnatomy(selected[0]);
                  }}
                  selected={selectedAnatomy ? [selectedAnatomy] : []}
                />
              </div>
              {
                selectedAnatomy && (
                  <div className="wizard-search-results">
                    <Button variant="primary" disabled={isCreatingAnatomy} onClick={() => {
                      setIsCreatingAnatomy(true);
                      setAnatomyCreationError(null);
                      APIWebVisualization(selectedAnatomy.id, "anatomy_set", selectedAnatomy.name, "", (res) => {
                        if (res) {
                          setCreatedVisual(res);
                          setIsCreatingAnatomy(false);
                          setSelectedAnatomy(null);
                          const visCacheCopy = shallowCopy(visCache);
                          visCacheCopy[res.id] = res;
                          setVisCache(visCacheCopy);
                          setStep(9);
                        } else {
                          setAnatomyCreationError('Failed to create anatomy explorer');
                          setIsCreatingAnatomy(false);
                        }
                      }, (err) => {
                        const errorMessage = (err?.message) || (err?.error?.[0]) || (err?.error) || ((typeof err === 'string') ? err : 'Failed to create anatomy explorer');
                        setAnatomyCreationError(errorMessage);
                        setIsCreatingAnatomy(false);
                      });
                    }} title={`Create Custom Anatomy Explorer for: ${selectedAnatomy.name}`}>
                      {isCreatingAnatomy ?<><Spinner animation="border" role="status" size="sm" variant="light" /> Creating...</> : `Create Custom Anatomy Explorer: ${selectedAnatomy.name}`}
                    </Button>
                  </div>
                )
              }
            </div>
          </div>
        )}

        {/* Step 3 for search (if hasOwnResources = false) */}
        {step === 3 && selectedContentType === 'standalone_visual' && hasOwnResources === false && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              setHasOwnResources(null);
              setAISearchTerm('');
              setSearchResults([]);
              // Return to whichever source-selection screen we came from
              if (visualType === 'chemistry') {
                // Already at step 3 chemistry — just reset hasOwnResources so chemistry picker re-shows
              } else if (creationMethod === 'manual') {
                // Came from hub sub-option with a visualType pre-set; go back to the resource question
                // (hasOwnResources=null re-triggers the step 3 non-chemistry render)
              } else {
                // Came through step 2.5; reset creationMethod so step 3 resource question re-shows
                setCreationMethod(null);
              }
            }}>← Back</Button>
            <h5 className="wizard-step-title">Search for Visuals</h5>

            <div className="wizard-upload-section">
              <p className="text-muted mb-3">Search Snorkle's visual database:</p>
              <SearchComponent
                isOrg={false}
                onVisualAdded={(result) => {
                  const visCacheCopy = shallowCopy(visCache);
                  result.isOrgVis = false;
                  visCacheCopy[result.id] = result;
                  setVisCache(visCacheCopy);
                  console.log('Visual added:', result);
                  // Show success screen instead of closing
                  setCreatedVisual(result);
                  setStep(9); // Success step
                }}
                lightTheme={true}
                onlyOneVisual={fromChannel}
                setIsUploading={setIsUploading}
              />
            </div>
          </div>
        )}

        {/* Step 4: Source Type Selection (if hasOwnResources = true) */}
        {step === 4 && selectedContentType === 'standalone_visual' && hasOwnResources === true && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              setHasOwnResources(null);
              setManualSourceType(null);
              setRequestDOI(false);
              setStep(3);
            }}>← Back</Button>
            <h5 className="wizard-step-title">What type of resource do you want to upload?</h5>

            <div className="creation-method-grid">
              <div
                className="creation-method-card"
                onClick={() => { setManualSourceType('file'); setStep(5); }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faImage} />
                </div>
                <h5>Images, Models, & Videos</h5>
                <p>Upload files from your computer</p>
              </div>

              <div
                className="creation-method-card"
                onClick={() => { setManualSourceType('web'); setRequestDOI(false); setStep(6); }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faRoute} />
                </div>
                <h5>Web Linked Resources</h5>
                <p>Link to web resources or molecules (PDB, PubChem)</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: DOI Request Question (only for file uploads) */}
        {step === 5 && selectedContentType === 'standalone_visual' && hasOwnResources === true && manualSourceType === 'file' && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              if (visualType === 'life_sciences') {
                // Came from PHI warning (step 4 was skipped)
                setManualSourceType(null);
                setStep('phi_warning');
              } else if (visualType === 'chemistry') {
                // Came from chemistry source picker — must also reset hasOwnResources
                // so the chemistry picker (which requires hasOwnResources === null) re-renders
                setHasOwnResources(null);
                setManualSourceType(null);
                setStep(3);
              } else if (visualType === 'videos_images') {
                // videos_images can only be reached via step 2.5 visual type picker
                setHasOwnResources(null);
                setManualSourceType(null);
                setStep(2.5);
              } else {
                // Normal path: came from step 4
                setManualSourceType(null);
                setStep(4);
              }
            }}>← Back</Button>
            <h5 className="wizard-step-title">Would you like to request a DOI for this visual?</h5>

            <div className="wizard-notice wizard-notice-crossref mb-4">
              <img src="/crossref-logo-200.svg" alt="Crossref" style={{ height: 38, flexShrink: 0 }} />
              <div>
                <strong>What is a DOI?</strong>
                <p className="mb-0 mt-1">A Digital Object Identifier (DOI) issued via <strong>Crossref</strong> provides a permanent, citable link to your visual that won't break over time — making it easy to reference in publications.</p>
              </div>
            </div>

            <div className="creation-method-grid">
              <div
                className="creation-method-card"
                onClick={() => { setRequestDOI(true); setStep(5.5); }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <h5>Yes, Request DOI</h5>
                <p>Get a permanent identifier for citation</p>
              </div>

              <div
                className="creation-method-card"
                onClick={() => { setRequestDOI(false); setStep(6); }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faPenToSquare} />
                </div>
                <h5>No DOI Needed</h5>
                <p>Continue without permanent identifier</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 5.5: DOI Instructions (if Yes was selected) */}
        {step === 5.5 && selectedContentType === 'standalone_visual' && hasOwnResources === true && manualSourceType === 'file' && requestDOI === true && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => setStep(5)}>← Back</Button>
            <h5 className="wizard-step-title">How to Request Your DOI</h5>

            <div className="wizard-notice wizard-notice-crossref mb-3">
              <img src="/crossref-logo-200.svg" alt="Crossref" style={{ height: 32, flexShrink: 0 }} />
              <span className="text-muted small">DOIs issued via Crossref</span>
            </div>

            <div className="wizard-notice wizard-notice-info mb-4">
              <h6><strong>After you create your visual:</strong></h6>
              <ol className="mb-0 mt-2">
                <li className="mb-2">Your visual will be saved to your library</li>
                <li className="mb-2">Visit the <strong>DOI Request Page</strong> at <a href="/doi" target="_blank">/doi</a></li>
                <li className="mb-2">Find your visual in the list and submit a DOI request</li>
                <li className="mb-0">You'll receive your DOI once it's been processed</li>
              </ol>
            </div>

            <div className="wizard-cta-row">
              <Button variant="primary" onClick={() => setStep(6)}>
                Continue to Upload
                <FontAwesomeIcon icon={faPlus} className="ms-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Upload Interface (Standalone with own resources) */}
        {step === 6 && selectedContentType === 'standalone_visual' && hasOwnResources === true && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => {
              console.log('⬅️ Back from Step 6, manualSourceType:', manualSourceType);
              setStep(manualSourceType === 'web' ? 4 : 5);
            }}>← Back</Button>
            <h5 className="wizard-step-title">Upload Your Resource</h5>

            {manualSourceType === 'file' && (
              <div className="wizard-upload-section">
                <p className="text-muted mb-3">
                  Upload images, 3D models, or videos{requestDOI && ' (DOI will be requested)'}:
                </p>
                <FileUploadForm
                  isLoggedIn={userInfo != null}
                  isOrg={false}
                  onVisualAdded={(result) => {
                    const visCacheCopy = shallowCopy(visCache);
                    result.isOrgVis = false;
                    visCacheCopy[result.id] = result;
                    setVisCache(visCacheCopy);
                    console.log('Visual added:', result);
                    // Show success screen instead of closing
                    setCreatedVisual(result);
                    setStep(9); // Success step
                  }}
                  lightTheme={false}
                  onCancel={handleClose}
                  onlyOneVisual={fromChannel}
                  setIsUploading={setIsUploading}
                />
              </div>
            )}

            {manualSourceType === 'web' && (
              <div className="wizard-upload-section">
                <p className="text-muted mb-3">
                  Link to web resources or molecules{requestDOI && ' (DOI will be requested)'}:
                </p>
                <WebUploadForm
                  isLoggedIn={userInfo != null}
                  isOrg={false}
                  onVisualAdded={(result) => {
                    const visCacheCopy = shallowCopy(visCache);
                    result.isOrgVis = false;
                    visCacheCopy[result.id] = result;
                    setVisCache(visCacheCopy);
                    console.log('Visual added:', result);
                    // Show success screen instead of closing
                    setCreatedVisual(result);
                    setStep(9); // Success step
                  }}
                  lightTheme={false}
                  onCancel={handleClose}
                  onlyOneVisual={fromChannel}
                  setIsUploading={setIsUploading}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 7: AI Configuration (for standalone visual AI brainstorming) */}
        {step === 7 && selectedContentType === 'standalone_visual' && creationMethod === 'ai' && (
          <div className="wizard-step wizard-config-section">
            <Button variant="link" onClick={() => { setCreationMethod(null); setHasOwnResources(false); setStep(3); }}>← Back to Search</Button>

            <div className="selected-type-summary">
              <div className="type-icon">
                <FontAwesomeIcon icon={faImage} />
              </div>
              <div>
                <h5>AI Visual Search Assistant</h5>
                <p>Let AI help you find the perfect visual from our database</p>
              </div>
            </div>

            <div className="preferences-section">
              <h5>What would you like to base the search on?</h5>
              <p className="helper-text">
                Provide context to help AI generate an effective search term
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Mission Statement</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={userSettings?.missionStatement || ''}
                  onChange={(e) => onUpdateUserSettings('missionStatement', e.target.value)}
                  placeholder="Your institution's mission or focus area..."
                />
                <Form.Text className="text-muted">
                  From your account settings
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Specific Topic or Theme</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={userSettings?.wizardPreferences?.specificPrompt || ''}
                  onChange={(e) => onUpdateUserSettings('wizardPreferences', {
                    ...userSettings?.wizardPreferences,
                    specificPrompt: e.target.value
                  })}
                  placeholder="What visual are you looking for? (e.g., 'heart anatomy cross-section', 'auditory pathway diagram')"
                />
              </Form.Group>
            </div>

            <div className="wizard-cta-row">
              <Button variant="outline-secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleGenerate('standalone_visual')}
                disabled={isGenerating || (!userSettings?.missionStatement && !userSettings?.wizardPreferences?.specificPrompt)}
              >
                <FontAwesomeIcon icon={faMagic} className="me-2" />
                Search with AI
              </Button>
            </div>
          </div>
        )}

        {/* Step 8: DOI Information for Scrollytelling */}
        {step === 8 && selectedContentType === 'scrollytelling' && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => setStep(2)}>← Back</Button>
            <h5 className="wizard-step-title">Would you like to request a DOI for your scrollytelling story?</h5>

            <div className="alert alert-info mb-4">
              <strong>What is a DOI?</strong>
              <p className="mb-2 mt-2">A Digital Object Identifier (DOI) provides a permanent, citable link to your content that won't break over time.</p>
              <p className="mb-0"><strong>How to request:</strong> After you create your scrollytelling story, visit the <a href="/doi" target="_blank">DOI request page</a> to submit your request.</p>
            </div>

            <div className="creation-method-grid">
              <div
                className="creation-method-card"
                onClick={() => {
                  setRequestDOI(true);
                  if (creationMethod === 'manual') {
                    navigate('/scrollytelling/builder');
                    handleClose();
                  } else {
                    setStep(3); // Go to AI config
                  }
                }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <h5>Yes, I'll Request a DOI</h5>
                <p>I'll visit the DOI page after creating my story</p>
              </div>

              <div
                className="creation-method-card"
                onClick={() => {
                  setRequestDOI(false);
                  if (creationMethod === 'manual') {
                    navigate('/scrollytelling/builder');
                    handleClose();
                  } else {
                    setStep(3); // Go to AI config
                  }
                }}
              >
                <div className="method-icon">
                  <FontAwesomeIcon icon={faPenToSquare} />
                </div>
                <h5>No DOI Needed</h5>
                <p>Continue without permanent identifier</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: AI Configuration (for scrollytelling and other content types, NOT standalone visual) */}
        {step === 3 && creationMethod === 'ai' && selectedContentType !== 'standalone_visual' && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => setStep(2)}>← Back</Button>
            <h5 className="wizard-step-title">Configure AI Generation</h5>

            <div className="wizard-config-section">
              <div className="selected-type-summary">
                <span className="type-icon">
                  <FontAwesomeIcon icon={selectedType.icon} />
                </span>
                <div>
                  <h5>{selectedType.name}</h5>
                  <p>AI will generate suggestions based on your inputs</p>
                </div>
              </div>

              {/* Preference Checkboxes */}
              <div className="preferences-section">
                {selectedContentType === 'scrollytelling' && (
                  <div className="mb-3">
                    <Button
                      variant="outline-primary"
                      className="w-100"
                      onClick={handleDraftScrollytellingFromPdf}
                    >
                      <FontAwesomeIcon icon={faMagic} className="me-2" />
                      Draft from PDF Instead
                    </Button>
                  </div>
                )}

                <h5>Base Suggestions On:</h5>
                <p className="helper-text">Select what information to use. At least one must be selected.</p>

                {(() => {
                  const prefs = userSettings?.wizardPreferences || {};
                  const missionActive = (prefs.useMissionStatement ?? !!userSettings?.missionStatement) && !!userSettings?.missionStatement;
                  const institutionActive = (prefs.useInstitutionProfile ?? !!userSettings?.institutionProfile) && !!userSettings?.institutionProfile;
                  const promptActive = !!prefs.specificPrompt;
                  const activeCount = [missionActive, institutionActive, promptActive].filter(Boolean).length;
                  return (
                    <>
                      <div className="pref-check">
                        <Form.Check
                          type="checkbox"
                          id="use-mission"
                          label="Mission Statement"
                          checked={missionActive}
                          disabled={!userSettings?.missionStatement || (missionActive && activeCount === 1)}
                          className="mb-0"
                          onChange={(e) => {
                            onUpdateUserSettings('wizardPreferences', { ...prefs, useMissionStatement: e.target.checked });
                          }}
                        />
                        {userSettings?.missionStatement && (
                          <Form.Group className="mt-2 ms-4">
                            <Form.Label className="pref-check-preview mb-1">Mission Statement (modify if needed)</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={prefs.missionStatementText ?? userSettings.missionStatement}
                              onChange={(e) => {
                                onUpdateUserSettings('wizardPreferences', { ...prefs, missionStatementText: e.target.value });
                              }}
                              className="prompt-input"
                            />
                          </Form.Group>
                        )}
                      </div>

                      <div className="pref-check">
                        <Form.Check
                          type="checkbox"
                          id="use-institution"
                          label={`Institution Profile ${!userSettings?.institutionProfile ? '(not set)' : ''}`}
                          checked={institutionActive}
                          disabled={!userSettings?.institutionProfile || (institutionActive && activeCount === 1)}
                          className="mb-0"
                          onChange={(e) => {
                            onUpdateUserSettings('wizardPreferences', { ...prefs, useInstitutionProfile: e.target.checked });
                          }}
                        />
                      </div>

                      <div className="pref-check">
                        <Form.Check
                          type="checkbox"
                          id="use-prompt"
                          label="Specific Prompt / Theme"
                          checked={promptActive}
                          disabled={promptActive && activeCount === 1}
                          className="mb-0"
                          onChange={(e) => {
                            onUpdateUserSettings('wizardPreferences', {
                              ...prefs,
                              specificPrompt: e.target.checked ? (prefs.specificPrompt || '') : ''
                            });
                          }}
                        />
                        {prefs.specificPrompt !== undefined && prefs.specificPrompt !== null && (
                          <Form.Group className="mt-2 ms-4">
                            <Form.Control
                              type="text"
                              placeholder='e.g., "Brain Awareness Week", "Heart Health Campaign"'
                              value={prefs.specificPrompt || ''}
                              onChange={(e) => {
                                onUpdateUserSettings('wizardPreferences', { ...prefs, specificPrompt: e.target.value });
                              }}
                              className="prompt-input"
                            />
                          </Form.Group>
                        )}
                      </div>
                    </>
                  );
                })()}

              </div>

              {/* Educational Level */}
              <div className="educational-level-section mt-4">
                <Form.Group>
                  <Form.Label>Target Audience Level</Form.Label>
                  <Form.Select
                    value={userSettings?.wizardPreferences?.educationalLevel || 'general'}
                    onChange={(e) => {
                      const prefs = userSettings?.wizardPreferences || {};
                      onUpdateUserSettings('wizardPreferences', { ...prefs, educationalLevel: e.target.value });
                    }}
                  >
                    <option value="elementary">Elementary / K-5</option>
                    <option value="middle">Middle School / 6-8</option>
                    <option value="high">High School / 9-12</option>
                    <option value="undergraduate">Undergraduate</option>
                    <option value="graduate">Graduate / Advanced</option>
                    <option value="professional">Professional / Continuing Education</option>
                    <option value="general">General Public</option>
                    <option value="researcher">Researchers / Specialists</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {/* Number of Slides (for scrollytelling only) */}
              {selectedContentType === 'scrollytelling' && (
                <div className="slides-count-section mt-4">
                  <Form.Group>
                    <Form.Label>Number of Slides</Form.Label>
                    <Form.Control
                      type="number"
                      min="3"
                      max="10"
                      value={userSettings?.wizardPreferences?.numSlides || 5}
                      onChange={(e) => {
                        const prefs = userSettings?.wizardPreferences || {};
                        const val = Math.min(Math.max(parseInt(e.target.value) || 5, 3), 10);
                        onUpdateUserSettings('wizardPreferences', { ...prefs, numSlides: val });
                      }}
                    />
                    <Form.Text muted>
                      Each slide will have a short text blurb (2-3 sentences) paired with a suggested background visual (3-10 slides)
                    </Form.Text>
                  </Form.Group>
                </div>
              )}

              {/* Output Language (for scrollytelling only) */}
              {selectedContentType === 'scrollytelling' && (
                <div className="mt-4">
                  <Form.Group>
                    <Form.Label>Output Language</Form.Label>
                    <Form.Select
                      value={userSettings?.wizardPreferences?.outputLanguage || 'en'}
                      onChange={(e) => {
                        const prefs = userSettings?.wizardPreferences || {};
                        onUpdateUserSettings('wizardPreferences', { ...prefs, outputLanguage: e.target.value });
                      }}
                    >
                      {Language_List.map((lang) => (
                        <option key={lang.code} value={lang.code}>{lang.name}</option>
                      ))}
                    </Form.Select>
                    <Form.Text muted>
                      The story text will be written in this language. Prompts and search terms should still be in English.
                    </Form.Text>
                  </Form.Group>
                </div>
              )}

              {/* Missing info alert */}
              {!userSettings?.missionStatement && !userSettings?.institutionProfile && (
                <div className="alert alert-warning mt-3">
                  <FontAwesomeIcon icon={faLightbulb} />
                  <span> For better suggestions, add your mission statement and institution profile in Settings</span>
                  <Button variant="link" className="plain-link" onClick={() => navigate('/dashboard/settings')}>Go to Settings</Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Manual Content Picker (for non-visual types) */}
        {step === 4 && creationMethod === 'manual' && selectedContentType !== 'standalone_visual' && (
          <div className="wizard-step">
            <Button variant="link" onClick={() => setStep(2)}>← Back</Button>
            <h5 className="wizard-step-title">Select {selectedType?.name}</h5>
            <p>Manual picker for {selectedContentType} would go here</p>
          </div>
        )}

        {/* Step 5: AI Results Display */}
        {step === 5 && creationMethod === 'ai' && generatedContent && (
          <div>
            <div className="wizard-header">
              <div className="success-icon">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <h4>Generated Content Ideas</h4>
              <p>{generatedContent.variations?.length > 1 ? `${generatedContent.variations.length} variations generated` : 'Review your AI-generated content concept below'}</p>
            </div>

            {generatedContent.variations?.map((variation, idx) => {
              // Handle slides structure from backend
              const title = variation.title;
              const abstract = variation.abstract;
              const slidesArray = Array.isArray(variation.slides) ? variation.slides : [];

              return (
                <div key={idx} className="generated-content-display mb-4">
                  {generatedContent.variations.length > 1 && (
                    <h5 className="variation-title">Variation {idx + 1}</h5>
                  )}

                  {title && (
                    <div className="story-title mb-3">
                      <h4>{title}</h4>
                    </div>
                  )}

                  {abstract && (
                    <div className="story-abstract mb-3">
                      <p className="text-muted">{abstract}</p>
                    </div>
                  )}

                  <div className="content-metadata">
                    {variation.papers_found && (
                      <span className="meta-badge">
                        📚 {variation.papers_found} papers found
                      </span>
                    )}
                    {variation.papers_used && (
                      <span className="meta-badge">
                        ✓ {variation.papers_used} papers used
                      </span>
                    )}
                    {variation.metadata?.is_mock && (
                      <span className="meta-badge mock-badge">
                        Mock Response
                      </span>
                    )}
                  </div>

                  <div className={slidesArray.length > 0 ? "content-body content-body--slides" : "content-body"}>
                    {slidesArray && slidesArray.length > 0 ? (
                      <div className="slides-display">
                        {slidesArray.map((slide, slideIdx) => (
                          <div key={slideIdx} className="slide-card">
                            <div className="slide-header">
                              <strong>{slide.section_title || `Slide ${slide.slide_number}`}</strong>
                              <span className="char-count">{slide.text_length}/350 chars</span>
                            </div>
                            <div className="slide-text">
                              <label>Text:</label>
                              <p>{slide.text}</p>
                            </div>
                            <div className="slide-visual">
                              <label>Suggested Visual:</label>
                              {(() => {
                                const visualRaw = slide.visual || '';
                                const match = visualRaw.match(/^(molecule|anatomy|imaging|sketchfab):\s*(.+)$/i);
                                if (match) {
                                  const typeLabels = {
                                    molecule: 'Molecule / PDB',
                                    anatomy: 'Anatomy Viewer',
                                    sketchfab: '3D Model'
                                  };
                                  const type = match[1].toLowerCase();
                                  const term = match[2].trim();
                                  return (
                                    <p className="visual-suggestion">
                                      <span className="visual-type-badge">{typeLabels[type] || type}</span>
                                      {' '}<strong>{term}</strong>
                                    </p>
                                  );
                                }
                                return <p className="visual-suggestion">{visualRaw}</p>;
                              })()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre>{variation.content}</pre>
                    )}
                  </div>

                  {variation.references && variation.references.length > 0 && (
                    <div className="references-section">
                      <h5>References ({variation.references.length})</h5>
                      <ol>
                        {variation.references.map((ref, i) => {
                          if (typeof ref === 'string') {
                            return <li key={i}>{ref}</li>;
                          }

                          // Helper to build valid URL
                          const getValidUrl = (ref) => {
                            if (ref.doi) {
                              // Clean DOI - remove 'https://doi.org/' if already present
                              const cleanDoi = ref.doi.replace(/^https?:\/\/(dx\.)?doi\.org\//, '');
                              return `https://doi.org/${cleanDoi}`;
                            }
                            if (ref.url) {
                              // Ensure URL has protocol
                              if (!ref.url.startsWith('http://') && !ref.url.startsWith('https://')) {
                                return `https://${ref.url}`;
                              }
                              return ref.url;
                            }
                            // Fallback: try to search PubMed by title
                            if (ref.title) {
                              return `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(ref.title)}`;
                            }
                            return null;
                          };

                          if (ref.citation) {
                            const validUrl = getValidUrl(ref);
                            return (
                              <li key={i}>
                                {ref.citation}
                                {validUrl && (
                                  <>
                                    {' '}
                                    <a
                                      href={validUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="reference-link"
                                    >
                                      [View Paper]
                                    </a>
                                  </>
                                )}
                              </li>
                            );
                          }

                          const citation = `${ref.authors || 'Unknown'} (${ref.year || 'n.d.'}). ${ref.title || 'Untitled'}. ${ref.journal ? `${ref.journal}.` : ''}`;
                          const validUrl = getValidUrl(ref);

                          return (
                            <li key={i}>
                              {citation}
                              {validUrl && (
                                <>
                                  {' '}
                                  <a
                                    href={validUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="reference-link"
                                  >
                                    [View Paper]
                                  </a>
                                </>
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Quiz Builder Step */}
        {step === 'quiz' && selectedContentType === 'quiz' && (
          <div className="wizard-step">
            {/* Full-screen overlay while saving quiz */}
            {isSavingQuiz && (
              <div style={{
                position: 'fixed', inset: 0,
                background: 'rgba(10, 8, 30, 0.65)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                zIndex: 9999,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: '1rem',
              }}>
                <div style={{
                  width: '48px', height: '48px',
                  border: '4px solid rgba(255,255,255,0.15)',
                  borderTop: '4px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'wizard-spin 0.85s linear infinite',
                }} />
                <p style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
                  Saving your quiz…
                </p>
              </div>
            )}
            <Button variant="link" onClick={handleBackToHub}>← Back</Button>
            <h5 className="wizard-step-title">Build Your Quiz</h5>

            <div className="quiz-builder-section">
              {/* Title */}
              <Form.Group className="mb-3">
                <Form.Label>Quiz Title <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Test Your Knowledge!"
                  value={quizTitle}
                  onChange={e => setQuizTitle(e.target.value)}
                />
              </Form.Group>

              {/* Question */}
              <Form.Group className="mb-3">
                <Form.Label>Question <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter your quiz question…"
                  value={quizQuestion}
                  onChange={e => setQuizQuestion(e.target.value)}
                />
              </Form.Group>

              {/* Answer options */}
              <Form.Group className="mb-3">
                <Form.Label>Answer Options <span className="text-danger">*</span></Form.Label>
                <p className="text-muted small mb-2">Select the radio button next to the correct answer.</p>
                {quizAnswers.map((answer, idx) => (
                  <div key={answer.id} className="quiz-answer-row mb-2">
                    <Form.Check
                      type="radio"
                      name="quiz-correct-answer"
                      checked={quizCorrectIndex === idx}
                      onChange={() => setQuizCorrectIndex(idx)}
                      className="quiz-answer-radio"
                      title="Mark as correct answer"
                      label=""
                    />
                    <Form.Control
                      type="text"
                      placeholder={`Answer ${idx + 1}`}
                      value={answer.text}
                      onChange={e => {
                        const updated = quizAnswers.map((a, i) =>
                          i === idx ? { ...a, text: e.target.value } : a
                        );
                        setQuizAnswers(updated);
                      }}
                    />
                    {quizAnswers.length > 2 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                          const updated = quizAnswers.filter((_, i) => i !== idx);
                          if (quizCorrectIndex >= updated.length) setQuizCorrectIndex(updated.length - 1);
                          setQuizAnswers(updated);
                        }}
                      >
                        ×
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-1"
                  onClick={() => setQuizAnswers([
                    ...quizAnswers,
                    { id: `ans-${Date.now()}-${quizAnswers.length}`, text: '' }
                  ])}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Answer
                </Button>
              </Form.Group>

              {/* Optional visual */}
              <Form.Group className="mb-4">
                <Form.Label>Background Visual <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <p className="text-muted small mb-2">
                  Select a visual from your library to display behind the quiz.
                </p>
                {quizVisual ? (
                  <div className="d-flex align-items-center gap-3 p-2 border rounded">
                    <img
                      src={quizVisual.thumbnail || 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg'}
                      alt={quizVisual.title}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }}
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{quizVisual.title}</div>
                      <div className="text-muted small">{quizVisual.description || 'No description'}</div>
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <Button variant="outline-primary" size="sm" onClick={() => setShowQuizVisualPicker(true)}>
                        Change
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => setQuizVisual(null)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline-primary" onClick={() => setShowQuizVisualPicker(true)}>
                    Select Visual
                  </Button>
                )}
              </Form.Group>

              {/* Error */}
              {quizSaveError && (
                <div className="alert alert-danger py-2">{quizSaveError}</div>
              )}

              {/* Save */}
              <div className="wizard-cta-row">
                <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={handleSaveQuiz}
                  disabled={isSavingQuiz || !quizQuestion.trim()}
                >
                  {isSavingQuiz ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving…
                    </>
                  ) : (
                    'Save Quiz'
                  )}
                </Button>
              </div>
            </div>

            {/* Visual picker modal */}
            {showQuizVisualPicker && (
              <VisualPickerModal
                show={showQuizVisualPicker}
                onHide={() => setShowQuizVisualPicker(false)}
                userInfo={userInfo}
                onSelect={(vis) => {
                  setQuizVisual(vis);
                  setShowQuizVisualPicker(false);
                }}
              />
            )}
          </div>
        )}

        {/* Step 9: Success Screen - Show Created Visual */}
        {step === 9 && createdVisual && (
          <div className="wizard-step text-center">
            <div className="success-icon mb-3">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h4 className="mb-3">Visual Created Successfully!</h4>
            <p className="text-muted mb-4">Your visual has been added to your library.</p>

            <div className="d-flex justify-content-center mb-4">
              <div style={{ maxWidth: '400px', width: '100%' }}>
                {selectedContentType === 'quiz' ? (
                  <CarouselPresentationCard
                    presentation={createdVisual}
                    blockActions={true}
                    isOrg={false}
                    canEdit={false}
                    canAddToChannel={false}
                  />
                ) : (
                  <CarouselVisCard
                    vis={createdVisual}
                    blockActions={true}
                    isOrg={false}
                    canEdit={false}
                    canAddToChannel={false}
                    showModal={false}
                  />
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <Button variant="outline-primary" onClick={() => {
                // Reset to create another
                setStep(1);
                setSelectedContentType(null);
                setCreationMethod(null);
                setHasOwnResources(null);
                setManualSourceType(null);
                setRequestDOI(false);
                setCreatedVisual(null);
              }}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create Another
              </Button>
              <Button variant="primary" onClick={handleClose}>
                Done
              </Button>
            </div>
          </div>
        )}
        {/* Visual Picking Step — one slide at a time after script approval */}
        {visualPickingMode && step === 5 && selectedContentType === 'scrollytelling' && pendingVariation && (() => {
          const slides = Array.isArray(pendingVariation.slides) ? pendingVariation.slides : [];
          const slide = slides[visualPickingIdx];
          if (!slide) return null;
          const searchState = slideSearchResults[visualPickingIdx] || { status: 'idle', results: [] };
          const visualRaw = slide.visual || '';
          const vtMatch = visualRaw.match(/^(molecule|anatomy|sketchfab):\s*(.+)$/i);
          const typeLabels = { molecule: 'Molecule / PDB', anatomy: 'Anatomy Viewer', sketchfab: '3D Model' };
          const selectedItem = approvedVisuals[visualPickingIdx] || null;

          return (
            <div className="wizard-step visual-picker-step">
              {isFinalizing && (
                <div className="wizard-generating-overlay">
                  <Spinner animation="border" variant="primary" style={{ width: '2.5rem', height: '2.5rem' }} />
                  <p className="mt-3 mb-0 fw-semibold text-primary">Creating visuals…</p>
                  <p className="text-muted" style={{ fontSize: '0.82rem' }}>Adding your selections to your library</p>
                </div>
              )}

              <div className="visual-picker-header">
                <span className="visual-picker-label">Pick a visual — slide {visualPickingIdx + 1} of {slides.length}</span>
                <div className="visual-picker-dots">
                  {slides.map((_, i) => (
                    <span
                      key={i}
                      className={`visual-picker-dot${i < visualPickingIdx ? ' done' : i === visualPickingIdx ? ' active' : ''}${approvedVisuals[i] ? ' has-pick' : ''}`}
                    />
                  ))}
                </div>
              </div>

              <div className="visual-picker-slide-info">
                <h6 className="mb-1">{slide.section_title || `Slide ${slide.slide_number}`}</h6>
                <p className="mb-2" style={{ fontSize: '0.88rem', color: '#374151' }}>{slide.text}</p>
                {vtMatch && (
                  <div className="mb-0">
                    <span className="visual-type-badge">{typeLabels[vtMatch[1].toLowerCase()] || vtMatch[1]}</span>
                    {' '}<strong style={{ fontSize: '0.84rem' }}>{vtMatch[2].trim()}</strong>
                  </div>
                )}
              </div>

              <div className="visual-picker-results">
                {searchState.status === 'loading' && (
                  <div className="d-flex flex-column align-items-center py-4">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>Searching for visuals…</p>
                  </div>
                )}
                {searchState.status === 'error' && (
                  <p className="text-muted text-center py-3 mb-0" style={{ fontSize: '0.85rem' }}>
                    Could not load results. You can skip and choose manually in the builder.
                  </p>
                )}
                {searchState.status === 'done' && searchState.results.length === 0 && (
                  <p className="text-muted text-center py-3 mb-0" style={{ fontSize: '0.85rem' }}>
                    No matching visuals found. You can skip and add one manually in the builder.
                  </p>
                )}
                {searchState.status === 'done' && searchState.results.length > 0 && (() => {
                  const visibleResults = showAllPickerResults
                    ? searchState.results
                    : searchState.results.slice(0, 4);
                  return (
                    <>
                      <div className="visual-picker-grid">
                        {visibleResults.map((item, i) => {
                          const thumb = getResultThumbnail(item);
                          const isSelected = selectedItem === item;
                          return (
                            <div
                              key={i}
                              className={`visual-picker-card${isSelected ? ' selected' : ''}`}
                              onClick={() => {
                                setApprovedVisuals(prev => {
                                  const next = [...prev];
                                  next[visualPickingIdx] = isSelected ? null : item;
                                  return next;
                                });
                              }}
                            >
                              <div className="visual-picker-card-thumb">
                                {thumb
                                  ? <img src={thumb} alt={item.title || ''} />
                                  : <div className="visual-picker-card-no-thumb"><FontAwesomeIcon icon={faCubes} /></div>
                                }
                              </div>
                              <div className="visual-picker-card-info">
                                <span className="visual-type-badge" style={{ fontSize: '0.62rem' }}>{getResultTypeLabel(item)}</span>
                                <p className="visual-picker-card-title">{item.title || '(Untitled)'}</p>
                              </div>
                              {isSelected && (
                                <div className="visual-picker-card-check">
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {searchState.results.length > 4 && (
                        <button
                          className="visual-picker-show-more"
                          onClick={() => setShowAllPickerResults(v => !v)}
                        >
                          {showAllPickerResults
                            ? `Show fewer`
                            : `Show all ${searchState.results.length} results`}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Custom pick display — shown when user picked from library */}
              {selectedItem?.visualization_url && (
                <div className="visual-picker-custom-pick">
                  <div className="visual-picker-custom-thumb">
                    {selectedItem.thumbnail
                      ? <img src={selectedItem.thumbnail} alt={selectedItem.title || ''} />
                      : <div className="visual-picker-card-no-thumb"><FontAwesomeIcon icon={faCubes} /></div>
                    }
                  </div>
                  <div className="visual-picker-custom-info">
                    <span className="visual-type-badge" style={{ fontSize: '0.68rem' }}>Custom pick</span>
                    <p className="mb-0" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>{selectedItem.title || '(Untitled)'}</p>
                  </div>
                  <button
                    className="visual-picker-custom-clear"
                    onClick={() => setApprovedVisuals(prev => { const n = [...prev]; n[visualPickingIdx] = null; return n; })}
                  >✕</button>
                </div>
              )}

              {/* Divider + "choose own" button */}
              <div className="visual-picker-own-row">
                <span className="visual-picker-own-divider">or</span>
                <button
                  className="visual-picker-own-btn"
                  onClick={() => setShowSlideVisualPicker(true)}
                >
                  Choose or create my own visual
                </button>
              </div>

              <div className="visual-picker-nav">
                <Button variant="outline-secondary" size="sm" onClick={handleVisualPickerBack}>
                  {visualPickingIdx === 0 ? '← Back to Script' : '← Previous'}
                </Button>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => handleVisualPickerAdvance(null)}>
                    {visualPickingIdx === slides.length - 1 ? 'Skip & Open Builder' : 'Skip →'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!selectedItem}
                    onClick={() => handleVisualPickerAdvance(selectedItem)}
                  >
                    {visualPickingIdx === slides.length - 1 ? 'Use Selected & Open Builder' : 'Use Selected & Next →'}
                  </Button>
                </div>
              </div>

              {/* Library / search visual picker for custom selection */}
              {showSlideVisualPicker && (
                <VisualPickerModal
                  show={showSlideVisualPicker}
                  onHide={() => setShowSlideVisualPicker(false)}
                  userInfo={userInfo}
                  initialSearchQuery={vtMatch ? vtMatch[2].trim() : ''}
                  defaultView={vtMatch ? 'search' : 'options'}
                  onSelect={(vis) => {
                    setApprovedVisuals(prev => {
                      const next = [...prev];
                      next[visualPickingIdx] = vis;
                      return next;
                    });
                    setShowSlideVisualPicker(false);
                  }}
                />
              )}
            </div>
          );
        })()}

      </Modal.Body>

      {/* Footer only rendered when it has meaningful action buttons */}
      {!visualPickingMode && ((step === 3 && creationMethod === 'ai') || (step === 5 && creationMethod === 'ai')) && (
        <Modal.Footer>
          {step === 3 && creationMethod === 'ai' && (
            <>
              <Button variant="outline-secondary" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleGenerate}
                disabled={isGenerating || (
                  !(( userSettings?.wizardPreferences?.useMissionStatement ?? !!userSettings?.missionStatement) && userSettings?.missionStatement) &&
                  !((userSettings?.wizardPreferences?.useInstitutionProfile ?? !!userSettings?.institutionProfile) && userSettings?.institutionProfile) &&
                  !userSettings?.wizardPreferences?.specificPrompt
                )}
              >
                {isGenerating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {selectedContentType === 'scrollytelling'
                      ? 'Generating story...'
                      : 'Generating...'
                    }
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagic} className="me-2" /> Generate Suggestions
                  </>
                )}
              </Button>
            </>
          )}

          {step === 5 && creationMethod === 'ai' && (() => {
            const hasValidSlides = selectedContentType === 'scrollytelling' &&
              generatedContent?.variations?.[0]?.slides &&
              Array.isArray(generatedContent.variations[0].slides) &&
              generatedContent.variations[0].slides.length > 0;

            return (
              <>
                <Button variant="outline-secondary" onClick={() => {
                  onReset();
                  setStep(1);
                  setCreationMethod(null);
                  setSelectedContentType(null);
                }}>
                  Generate Another
                </Button>
                {hasValidSlides && (
                  <Button
                    variant="primary"
                    onClick={() => handleEnterVisualPicker(generatedContent.variations[0])}
                  >
                    Select Visuals & Continue →
                  </Button>
                )}
                <Button variant="outline-secondary" onClick={handleClose}>
                  Close
                </Button>
              </>
            );
          })()}
        </Modal.Footer>
      )}
    </Modal>

    <PremiumFeatureModal
      show={showPremiumModal}
      onHide={() => setShowPremiumModal(false)}
      featureName={premiumFeatureName}
    />

    {ReactDOM.createPortal(
      <AccessCodeModal
        show={showAccessCodeModal}
        onHide={() => setShowAccessCodeModal(false)}
        featureName={accessCodeFeatureName}
        featureKey={accessCodePermission}
        requiredCode={accessCodePermission ? "" :"demo"}
        onAccessGranted={() => {
          setShowAccessCodeModal(false);
          if (accessCodeAction) accessCodeAction();
        }}
      />,
      document.body
    )}

    {hoveredHubPreview && ReactDOM.createPortal(
      <div
        className="hub-gif-preview-portal"
        style={{
          position: 'fixed',
          left: hoveredHubPreview.rect.left + hoveredHubPreview.rect.width / 2,
          top: hoveredHubPreview.rect.top,
          transform: 'translate(-50%, calc(-100% - 12px))',
          zIndex: 2000,
          pointerEvents: 'none',
        }}
      >
        <div
          className="hub-card-preview-popup hub-card-preview-popup--visible"
          style={hoveredHubPreview.width ? { width: hoveredHubPreview.width } : undefined}
        >
          <img src={hoveredHubPreview.gif} alt="Preview" className="hub-card-preview-gif" />
          <div className="hub-card-preview-label">Preview</div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default ContentCreationWizard;
